import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import {
  CheckCheck,
  Heart,
  MessageCircle,
  MoreVertical,
  Phone,
  Search,
  Send,
  User,
  UserCheck,
  Video,
} from 'lucide-react';
import { useApp } from '../../context/AppContext';
import { useAuth } from '../../context/AuthContext';
import { getApiErrorMessage } from '../../lib/apiError';

const profileIdsFor = (currentUser) => new Set([
  currentUser?.id,
  currentUser?.profileId,
  currentUser?.userId,
  currentUser?._id,
].filter(Boolean));

const participantNamesFor = (conversation) => conversation?.participantNames || conversation?.participant_names || [];
const participantIdsFor = (conversation) => conversation?.participants || [];
const lastMessageFor = (conversation) => conversation?.lastMessage || conversation?.last_message || null;
const unreadCountFor = (conversation) => conversation?.unreadCount || conversation?.unread_count || {};

const messageSenderId = (message) => message?.senderId || message?.sender_id;
const messageTimestamp = (message) => message?.timestamp || message?.createdAt || message?.created_at || message?.time;
const messageReadBy = (message) => message?.readBy || message?.read_by || [];

const MessagesPage = () => {
  const { authApi } = useAuth();
  const {
    currentUser,
    conversations,
    getUserConversations,
    getConversationById,
    sendConversationMessage,
  } = useApp();
  const [searchParams, setSearchParams] = useSearchParams();
  const requestedConversationId = searchParams.get('conversation');
  const [selectedConversationId, setSelectedConversationId] = useState(requestedConversationId || null);
  const [selectedMessages, setSelectedMessages] = useState([]);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [sending, setSending] = useState(false);
  const [newMessage, setNewMessage] = useState('');
  const [searchQuery, setSearchQuery] = useState('');

  const userIds = useMemo(() => profileIdsFor(currentUser), [currentUser]);
  const userConversations = useMemo(() => {
    const matched = getUserConversations(currentUser?.id) || [];
    if (matched.length > 0) return matched;
    return (conversations || []).filter((conversation) => participantIdsFor(conversation).some((id) => userIds.has(id)));
  }, [conversations, currentUser?.id, getUserConversations, userIds]);
  const selectedConversation = selectedConversationId ? getConversationById(selectedConversationId) : userConversations[0];

  useEffect(() => {
    if (requestedConversationId) {
      setSelectedConversationId(requestedConversationId);
    }
  }, [requestedConversationId]);

  useEffect(() => {
    if (!selectedConversationId && userConversations.length > 0) {
      const firstId = userConversations[0].id;
      setSelectedConversationId(firstId);
      setSearchParams({ conversation: firstId }, { replace: true });
    }
  }, [selectedConversationId, setSearchParams, userConversations]);

  useEffect(() => {
    if (!selectedConversation?.id) {
      setSelectedMessages([]);
      return;
    }
    let cancelled = false;
    const loadMessages = async () => {
      setLoadingMessages(true);
      try {
        const response = await authApi.get(`/conversations/${selectedConversation.id}/messages`);
        if (!cancelled) setSelectedMessages(response.data || []);
      } catch (error) {
        if (!cancelled) {
          setSelectedMessages(selectedConversation.messages || []);
          toast.error(getApiErrorMessage(error, 'Unable to load conversation messages.'));
        }
      } finally {
        if (!cancelled) setLoadingMessages(false);
      }
    };
    loadMessages();
    return () => {
      cancelled = true;
    };
  }, [authApi, selectedConversation?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const getConversationTypeIcon = (type) => {
    switch (type) {
      case 'Family': return Heart;
      case 'CareSupport': return UserCheck;
      default: return User;
    }
  };

  const getOtherParticipantName = (conversation) => {
    const names = participantNamesFor(conversation);
    const firstName = currentUser?.firstName || '';
    return names.find(name => firstName && !name.includes(firstName)) || names.find(Boolean) || 'Unknown';
  };

  const selectConversation = (conversationId) => {
    setSelectedConversationId(conversationId);
    setSearchParams({ conversation: conversationId });
  };

  const formatTime = (timestamp) => {
    if (!timestamp) return '';
    const date = new Date(timestamp);
    if (Number.isNaN(date.getTime())) return '';
    const now = new Date();
    const diffDays = Math.floor((now - date) / (1000 * 60 * 60 * 24));

    if (diffDays === 0) {
      return date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
    }
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return date.toLocaleDateString('en-US', { weekday: 'short' });
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const filteredConversations = userConversations.filter(conversation => (
    getOtherParticipantName(conversation).toLowerCase().includes(searchQuery.toLowerCase())
  ));

  const handleSendMessage = async (event) => {
    event.preventDefault();
    const text = newMessage.trim();
    if (!text || !selectedConversation?.id) return;
    setSending(true);
    try {
      const message = await sendConversationMessage(selectedConversation.id, text);
      setSelectedMessages(prev => [...prev, message]);
      setNewMessage('');
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Unable to send message.'));
    } finally {
      setSending(false);
    }
  };

  const renderConversationList = (items, title) => {
    if (items.length === 0) return null;
    return (
      <div>
        <div className="bg-[#F7F4EE] px-4 py-2">
          <span className="text-xs font-medium uppercase tracking-[0.12em] text-[#5A5A5A]">{title}</span>
        </div>
        {items.map((conversation) => {
          const isSelected = selectedConversation?.id === conversation.id;
          const unread = unreadCountFor(conversation)?.[currentUser?.id] || 0;
          const lastMessage = lastMessageFor(conversation);
          const TypeIcon = getConversationTypeIcon(conversation.type);
          const otherName = getOtherParticipantName(conversation);

          return (
            <button
              key={conversation.id}
              type="button"
              onClick={() => selectConversation(conversation.id)}
              className={`block w-full border-l-4 px-4 py-3 text-left transition-colors ${
                isSelected ? 'border-[#9F1D35] bg-[#F5E8EA]' : 'border-transparent hover:bg-[#F7F4EE]'
              }`}
              data-testid={`conversation-${conversation.id}`}
            >
              <div className="flex items-center gap-3">
                <div className={`flex h-12 w-12 items-center justify-center rounded-full font-bold text-[#FAFAF7] ${
                  conversation.type === 'Family' ? 'bg-[#A33232]' : 'bg-[#9F1D35]'
                }`}>
                  <TypeIcon className="h-5 w-5" />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center justify-between gap-3">
                    <p className="truncate font-medium text-[#1A1A1A]">{otherName}</p>
                    <span className="text-xs text-[#5A5A5A]">{formatTime(lastMessage?.timestamp)}</span>
                  </div>
                  <div className="flex items-center justify-between gap-2">
                    <p className="truncate text-sm text-[#5A5A5A]">{lastMessage?.text || 'No messages yet'}</p>
                    {unread > 0 && (
                      <span className="ml-2 flex h-5 min-w-5 items-center justify-center rounded-full bg-[#9F1D35] px-1 text-xs text-[#FAFAF7]">
                        {unread}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </button>
          );
        })}
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-[#F7F4EE]" data-testid="messages-page">
      <div className="flex h-[calc(100vh-80px)] flex-col md:flex-row">
        <div className="flex w-full flex-col border-r border-[#E8E6E3] bg-[#FAFAF7] md:w-80">
          <div className="border-b border-[#E8E6E3] p-4">
            <h2 className="mb-3 font-serif text-xl font-bold text-[#1A1A1A]">Messages</h2>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[#5A5A5A]" />
              <input
                type="text"
                placeholder="Search conversations..."
                value={searchQuery}
                onChange={(event) => setSearchQuery(event.target.value)}
                className="w-full rounded-full border border-[#E8E6E3] bg-white py-2 pl-10 pr-4 text-sm focus:border-[#9F1D35] focus:outline-none"
              />
            </div>
          </div>

          <div className="max-h-72 flex-1 overflow-y-auto md:max-h-none">
            {renderConversationList(filteredConversations.filter(c => c.type === 'Family'), 'Family')}
            {renderConversationList(filteredConversations.filter(c => c.type === 'CareSupport'), 'Care Team')}
            {filteredConversations.length === 0 && (
              <div className="p-8 text-center">
                <MessageCircle className="mx-auto mb-3 h-12 w-12 text-[#D8D5D0]" />
                <p className="text-[#5A5A5A]">No conversations found</p>
              </div>
            )}
          </div>
        </div>

        <div className="flex min-h-0 flex-1 flex-col bg-[#F7F4EE]">
          {selectedConversation ? (
            <>
              <div className="flex items-center justify-between border-b border-[#E8E6E3] bg-[#FAFAF7] px-4 py-4 sm:px-6">
                <div className="flex items-center gap-4">
                  <div className={`flex h-12 w-12 items-center justify-center rounded-full font-bold text-[#FAFAF7] ${
                    selectedConversation.type === 'Family' ? 'bg-[#A33232]' : 'bg-[#9F1D35]'
                  }`}>
                    {getOtherParticipantName(selectedConversation).charAt(0)}
                  </div>
                  <div>
                    <h3 className="font-semibold text-[#1A1A1A]">{getOtherParticipantName(selectedConversation)}</h3>
                    <p className="text-sm text-[#5A5A5A]">
                      {selectedConversation.type === 'Family' ? 'Family' : 'Care officer'}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <button className="rounded-full p-2 text-[#5A5A5A] hover:bg-[#F5E8EA] hover:text-[#9F1D35]">
                    <Phone className="h-5 w-5" />
                  </button>
                  <button className="rounded-full p-2 text-[#5A5A5A] hover:bg-[#F5E8EA] hover:text-[#9F1D35]">
                    <Video className="h-5 w-5" />
                  </button>
                  <button className="rounded-full p-2 text-[#5A5A5A] hover:bg-[#F5E8EA]">
                    <MoreVertical className="h-5 w-5" />
                  </button>
                </div>
              </div>

              <div className="flex-1 overflow-y-auto p-4 sm:p-6">
                {loadingMessages ? (
                  <p className="text-center text-sm text-[#5A5A5A]">Loading messages...</p>
                ) : (
                  <div className="space-y-4">
                    {selectedMessages.map((message, index) => {
                      const isCurrentUser = userIds.has(messageSenderId(message));
                      return (
                        <div key={message.id || index} className={`flex ${isCurrentUser ? 'justify-end' : 'justify-start'}`}>
                          <div className="max-w-[78%]">
                            <div className={`rounded-2xl px-4 py-3 ${
                              isCurrentUser
                                ? 'rounded-tr-sm bg-[#9F1D35] text-[#FAFAF7]'
                                : 'rounded-tl-sm border border-[#E8E6E3] bg-[#FAFAF7] text-[#1A1A1A]'
                            }`}>
                              <p className="text-sm">{message.text}</p>
                            </div>
                            <div className={`mt-1 flex items-center gap-1 ${isCurrentUser ? 'justify-end' : ''}`}>
                              <span className="text-xs text-[#5A5A5A]">{formatTime(messageTimestamp(message))}</span>
                              {isCurrentUser && messageReadBy(message).length > 1 && <CheckCheck className="h-3 w-3 text-[#9F1D35]" />}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                    {selectedMessages.length === 0 && (
                      <div className="py-16 text-center">
                        <MessageCircle className="mx-auto mb-3 h-12 w-12 text-[#D8D5D0]" />
                        <p className="font-medium text-[#1A1A1A]">No messages yet</p>
                        <p className="text-sm text-[#5A5A5A]">Send the first message to begin this care-team thread.</p>
                      </div>
                    )}
                  </div>
                )}
              </div>

              <div className="border-t border-[#E8E6E3] bg-[#FAFAF7] p-4">
                <form onSubmit={handleSendMessage} className="flex items-center gap-3">
                  <input
                    type="text"
                    value={newMessage}
                    onChange={(event) => setNewMessage(event.target.value)}
                    placeholder="Type your message..."
                    className="flex-1 rounded-full border border-[#E8E6E3] bg-white px-4 py-3 text-sm focus:border-[#9F1D35] focus:outline-none"
                  />
                  <button
                    type="submit"
                    disabled={!newMessage.trim() || sending}
                    className="rounded-full bg-[#9F1D35] p-3 text-[#FAFAF7] transition-colors hover:bg-[#7A1628] disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    <Send className="h-5 w-5" />
                  </button>
                </form>
              </div>
            </>
          ) : (
            <div className="flex flex-1 items-center justify-center">
              <div className="text-center">
                <MessageCircle className="mx-auto mb-4 h-16 w-16 text-[#D8D5D0]" />
                <h3 className="mb-2 text-xl font-semibold text-[#1A1A1A]">Select a conversation</h3>
                <p className="text-[#5A5A5A]">Choose a conversation from the list to start messaging.</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default MessagesPage;
